import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

// APIs, Context & Hooks
import { baseAxios } from "../../apis";
import { useBotDemo } from "../../context/botDemoContext";

// Components
import DrawerActivateBot1Demo from "./DrawerActivateBot1Demo";
import DrawerActivateBot2Demo from "./DrawerActivateBot2Demo";
import DrawerActivateBot3Demo from "./DrawerActivateBot3Demo";

// --- CONSTANTS (Mimicking Main Bot Structure) ---

const BOT_STRATEGIES = {
  REBALANCE: "rebalance",
  GRID: "grid",
  XENO_ARC: "xeno arc",
  XENO_V3: "xeno bot v3",
  DCA: "dca",
};

const BOT_ACTIVATE_STEPS = {
  STEP_1: "step-bot-1",
  STEP_2: "step-bot-2",
  STEP_3: "step-bot-3",
  STEP_2_EDIT: "step-bot-2-edit",
  STEP_3_EDIT: "step-bot-3-edit",
};

// --- INITIAL SETTINGS ---

export const initSettingRebalance = {
  capital: 0,
  pattern: 0,
  divergence_rate: 0,
  time_designation: 0,
  take_profit_status: false,
  take_profit_value: 0,
  take_profit_percentage: 0,
  follow_market_cap: false,
  allocations: [],
};

export const initSettingGrid = {
  amount: "",
  grid_width: "",
  grid_width_exit: "",
  number_of_grids: "",
  trade_size: "",
  buy_after_bounce: "",
  sell_after_bounce: "",
};

export const initSettingXenoV2 = {
  amount: "",
  grid_width: "",
  buy_flg_return: "",
  grid_width_exit: "",
  exit_flg_return: "",
  number_of_grids: "",
  capital: "",
};

export const initSettingXenoV3 = {
  amount: 0,
  grid_width: 0,
  buy_flg_return: 0,
  grid_width_exit: 0,
  exit_flg_return: 0,
  grid_exit_ratio: 0,
  max_drawdown: 0,
  dd_base_point_all_exit: 0,
  exit_when_touch_dd: 0,
  exit_when_non_touch_dd: 0,
  all_exit_flg_return: 0,
};

export const initSettingDCA = {
  capital: "",
  amount: "",
  max_order_count: "",
  price_change_percentage: "",
  take_profit_percentage: "",
  trigger_price: "",
  period: 0,
};

// --- MAIN COMPONENT ---

const DrawerActivateBotDemo = ({
  openDialog,
  setOpenDialog,
  setSelectedBot,
  botDetail,
  bots,
  selectedBot,
}) => {
  const location = useLocation();
  const { myExchanges, fetchExchangesDemo } = useBotDemo();

  // --- State ---
  const [formRebalance, setFormRebalance] = useState({
    settings: initSettingRebalance,
  });
  const [formDCA, setFormDCA] = useState({ settings: initSettingDCA });
  const [formXenoGrid, setFormXenoGrid] = useState({
    settings: initSettingGrid,
  });
  const [formXenoV2, setFormXenoV2] = useState({ settings: initSettingXenoV2 });
  const [formXenoV3, setFormXenoV3] = useState({ settings: initSettingXenoV3 });

  const [loadingInitialData, setLoadingInitialData] = useState(false);
  const hasFetched = useRef(false);

  // Fetch exchanges once when the component is mounted
  useEffect(() => {
    fetchExchangesDemo(true);
  }, [fetchExchangesDemo]);

  // --- Form Population (Edit Mode) ---
  // useEffect(() => {
  //   const fetchAndSetEditData = async () => {
  //     // Validation to ensure we have necessary data
  //     if (!botDetail || !selectedBot || myExchanges.length === 0) {
  //       return;
  //     }

  //     setLoadingInitialData(true);

  //     try {
  //       const targetExchange = myExchanges.find(
  //         (ex) =>
  //           ex.exchange.toLowerCase() === botDetail.exchange_name.toLowerCase()
  //       );

  //       if (!targetExchange) {
  //         console.error("This bot's exchange is not connected.");
  //         setLoadingInitialData(false);
  //         return;
  //       }

  //       // Fetch Demo Balance (Specific to Demo implementation)
  //       const { data } = await baseAxios.get(`/api/demo/bot_create/balance`, {
  //         params: {
  //           coin: botDetail.coin_name.split("/")[0],
  //           base: botDetail.coin_name.split("/")[1],
  //         },
  //       });

  //       const specificExchange = data.my_exchanges.find(
  //         (ex) => ex.exchange === botDetail.exchange_name
  //       );
  //       const userBalance = specificExchange?.demo_balance || 0;

  //       // Convert settings to numbers
  //       const convertedSettings = {};
  //       for (const key in botDetail.setting) {
  //         const value = botDetail.setting[key];
  //         if (typeof value === "boolean" || value === "") {
  //           convertedSettings[key] = value;
  //         } else {
  //           convertedSettings[key] = Number(value) || 0;
  //         }
  //       }

  //       const editFormData = {
  //         coin_id: selectedBot.coin_id || 0,
  //         bot_master_id: selectedBot.id || 0,
  //         exchange_name: botDetail.exchange_name || "",
  //         coin_name: botDetail.coin_name || "",
  //         userBalance, // Demo specific field
  //         settings: convertedSettings,
  //       };

  //       const strategy = selectedBot?.bot_name?.toLowerCase();

  //       // Set the appropriate form based on strategy
  //       switch (strategy) {
  //         case BOT_STRATEGIES.GRID:
  //           setFormXenoGrid(editFormData);
  //           break;
  //         case BOT_STRATEGIES.XENO_ARC:
  //           setFormXenoV2(editFormData);
  //           break;
  //         case BOT_STRATEGIES.XENO_V3:
  //           setFormXenoV3(editFormData);
  //           break;
  //         case BOT_STRATEGIES.DCA:
  //           setFormDCA(editFormData);
  //           break;
  //         default:
  //           setFormRebalance(editFormData);
  //           break;
  //       }
  //     } catch (error) {
  //       console.error("Failed to fetch initial data for editing:", error);
  //     } finally {
  //       setLoadingInitialData(false);
  //     }
  //   };

  //   // Check if step 2 edit is open and we haven't fetched yet
  //   if (openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT && !hasFetched.current) {
  //     fetchAndSetEditData();
  //     hasFetched.current = true;
  //   }

  //   // Cleanup ref when dialog closes
  //   return () => {
  //     if (openDialog !== BOT_ACTIVATE_STEPS.STEP_2_EDIT) {
  //       hasFetched.current = false;
  //     }
  //   };
  // }, [openDialog, botDetail, selectedBot, myExchanges]);

  useEffect(() => {
    if (openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT && botDetail) {
      const strategy = selectedBot?.bot_name?.toLowerCase();

      // Convert all settings to numbers
      const convertedSettings = {};
      for (const key in botDetail.setting) {
        const value = botDetail.setting[key];
        if (typeof value === "boolean" || value === "") {
          convertedSettings[key] = value;
        } else {
          convertedSettings[key] = Number(value) || 0;
        }
      }

      const editFormData = {
        coin_id: selectedBot.coin_id || 0,
        bot_master_id: selectedBot.id || 0,
        exchange_name: botDetail.exchange_name || "",
        coin_name: botDetail.coin_name || "",
        settings: convertedSettings,
      };

      // Set the appropriate form based on strategy
      switch (strategy) {
        case BOT_STRATEGIES.GRID:
          setFormXenoGrid(editFormData);
          break;
        case BOT_STRATEGIES.XENO_ARC:
          setFormXenoV2(editFormData);
          break;
        case BOT_STRATEGIES.XENO_V3:
          setFormXenoV3(editFormData);
          break;
        case BOT_STRATEGIES.DCA:
          setFormDCA(editFormData);
          break;
        default:
          setFormRebalance(editFormData);
          break;
      }
    }
  }, [openDialog, botDetail, selectedBot]);

  // --- Reset Forms ---
  useEffect(() => {
    if (openDialog === BOT_ACTIVATE_STEPS.STEP_1 || !openDialog) {
      setFormXenoGrid({ settings: initSettingGrid });
      setFormXenoV2({ settings: initSettingXenoV2 });
      setFormXenoV3({ settings: initSettingXenoV3 });
      setFormRebalance({ settings: initSettingRebalance });
      setFormDCA({ settings: initSettingDCA });
    }
  }, [openDialog]);

  // --- Handlers ---

  const handleClose = () => {
    setOpenDialog(null);
  };

  const isEditMode =
    openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT ||
    openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT;
  const isBotListPage = location.pathname.includes("bot-list");

  const handleBackFromStep2 = () => {
    setOpenDialog(isBotListPage ? null : BOT_ACTIVATE_STEPS.STEP_1);
  };

  const handleNextFromStep2 = () =>
    setOpenDialog(
      isEditMode ? BOT_ACTIVATE_STEPS.STEP_3_EDIT : BOT_ACTIVATE_STEPS.STEP_3
    );

  const handleBackFromStep3 = () =>
    setOpenDialog(
      isEditMode ? BOT_ACTIVATE_STEPS.STEP_2_EDIT : BOT_ACTIVATE_STEPS.STEP_2
    );

  // --- Memoized Active Form ---
  const activeForm = useMemo(() => {
    const strategy = selectedBot?.bot_name?.toLowerCase();
    switch (strategy) {
      case BOT_STRATEGIES.GRID:
        return formXenoGrid;
      case BOT_STRATEGIES.XENO_ARC:
        return formXenoV2;
      case BOT_STRATEGIES.XENO_V3:
        return formXenoV3;
      case BOT_STRATEGIES.DCA:
        return formDCA;
      default:
        return formRebalance;
    }
  }, [
    selectedBot,
    formRebalance,
    formDCA,
    formXenoGrid,
    formXenoV2,
    formXenoV3,
  ]);

  return (
    <>
      <DrawerActivateBot1Demo
        open={openDialog === BOT_ACTIVATE_STEPS.STEP_1}
        onClose={handleClose}
        setSelectedBot={setSelectedBot}
        onNext={() => setOpenDialog(BOT_ACTIVATE_STEPS.STEP_2)}
        bots={bots}
      />
      <DrawerActivateBot2Demo
        open={
          openDialog === BOT_ACTIVATE_STEPS.STEP_2 ||
          openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT
        }
        onClose={handleClose}
        onNext={handleNextFromStep2}
        onBack={handleBackFromStep2}
        selectedBot={selectedBot}
        formRebalance={formRebalance}
        setFormRebalance={setFormRebalance}
        formXenoV2={formXenoV2}
        setFormXenoV2={setFormXenoV2}
        formXenoGrid={formXenoGrid}
        setFormXenoGrid={setFormXenoGrid}
        formDCA={formDCA}
        setFormDCA={setFormDCA}
        formXenoV3={formXenoV3}
        setFormXenoV3={setFormXenoV3}
        isEdit={openDialog === BOT_ACTIVATE_STEPS.STEP_2_EDIT}
        isLoading={loadingInitialData}
      />
      <DrawerActivateBot3Demo
        open={
          openDialog === BOT_ACTIVATE_STEPS.STEP_3 ||
          openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT
        }
        onBack={handleBackFromStep3}
        onClose={handleClose}
        selectedBot={selectedBot}
        onConfirm={handleClose}
        isEdit={openDialog === BOT_ACTIVATE_STEPS.STEP_3_EDIT}
        form={activeForm}
      />
    </>
  );
};

export default DrawerActivateBotDemo;
